import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  DollarSign, RefreshCw, Download, Search, Calendar,
  ChevronDown, ChevronUp, SlidersHorizontal, Loader2,
  Clock, CheckCircle, AlertTriangle, XCircle, Edit, ArrowUpDown,
  ArrowUp, ArrowDown, TrendingUp, Banknote, CreditCard, Wallet,
  Printer, Columns, FileSpreadsheet
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MONTH_LABELS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const FinancialOverviewPage = () => {
  const token = localStorage.getItem("token");
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState("");
  const [clientType, setClientType] = useState("all");
  const [records, setRecords] = useState([]);
  const [gpConfigs, setGpConfigs] = useState({});
  const [maxPartners, setMaxPartners] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [settlementStatus, setSettlementStatus] = useState("all");
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "client_code", direction: "asc" });
  const [activeTab, setActiveTab] = useState("overview");
  const [rawData, setRawData] = useState({ raw_funds: [], crdr_net: [], narrations: {}, new_clients: [], client_info: {} });
  const [rawSortConfig, setRawSortConfig] = useState({ key: "client_code", direction: "asc" });
  const [rawNarrEdit, setRawNarrEdit] = useState(null); // {key, client_code, month, value}
  const [printRawColumns, setPrintRawColumns] = useState({
    client: true, month: true, parcels: true, costing: true, sales: true,
    raw_funds: true, sale_minus_raw: true, cost_minus_raw: true,
  });

  // Edit dialog
  const [editDialog, setEditDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editNarration, setEditNarration] = useState("");
  const [editStatus, setEditStatus] = useState("pending");

  // Print/Export dialog
  const [printDialog, setPrintDialog] = useState(false);
  const [printPartners, setPrintPartners] = useState(["all"]); // ["all"] or specific client_codes
  const [printColumns, setPrintColumns] = useState({
    client: true, month: true, parcels: true, costing: true, sales: true,
    total_gp: true, gp_columns: true, payments: true, credits: true,
    debits: true, partner_payment: true, net_position: true, status: true, narration: false,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year });
      if (month) params.append("month", month);
      if (clientType !== "all") params.append("client_type", clientType);

      const response = await fetch(`${BACKEND_URL}/api/financial-overview/monthly-summary?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRecords(data.records || []);
        setGpConfigs(data.gp_configs || {});
        setMaxPartners(data.max_partners || 0);
      } else {
        toast.error("Failed to fetch data");
      }
    } catch {
      toast.error("Error loading financial data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token, year, month, clientType]);

  const fetchRawFunds = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/financial-overview/raw-funds-summary?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setRawData(await res.json());
    } catch {}
  };

  useEffect(() => {
    if (token) fetchRawFunds();
  }, [token, year]);

  const openEditDialog = (record) => {
    setEditingRecord(record);
    setEditNarration(record.narration || "");
    setEditStatus(record.settlement_status || "pending");
    setEditDialog(true);
  };

  const handleSaveSettlement = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/financial-overview/update-settlement`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          client_code: editingRecord.client_code,
          year: editingRecord.year,
          month: editingRecord.month,
          settlement_status: editStatus,
          narration: editNarration,
        }),
      });
      if (!response.ok) throw new Error("Failed to update");
      toast.success("Settlement updated");
      setEditDialog(false);
      fetchData();
    } catch {
      toast.error("Failed to update settlement");
    }
  };

  // Sort handler
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortConfig.direction === "asc"
      ? <ArrowUp className="w-3 h-3 ml-1 text-emerald-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-emerald-600" />;
  };

  // Filter & sort records
  const filteredRecords = useMemo(() => {
    let result = records.filter((r) => {
      if (settlementStatus !== "all" && r.settlement_status !== settlementStatus) return false;
      if (searchTerm) {
        return (
          r.client_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.client_name?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      return true;
    });

    if (sortConfig.key) {
      result = [...result].sort((a, b) => {
        const aVal = a[sortConfig.key] ?? "";
        const bVal = b[sortConfig.key] ?? "";
        if (typeof aVal === "number" && typeof bVal === "number") {
          return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
        }
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        if (aStr < bStr) return sortConfig.direction === "asc" ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [records, searchTerm, settlementStatus, sortConfig]);

  // Totals
  const totals = useMemo(() => ({
    parcels: filteredRecords.reduce((s, r) => s + (r.parcels || 0), 0),
    costing: filteredRecords.reduce((s, r) => s + (r.costing || 0), 0),
    sales: filteredRecords.reduce((s, r) => s + (r.sales || 0), 0),
    total_gp: filteredRecords.reduce((s, r) => s + (r.total_gp || 0), 0),
    verified_payments: filteredRecords.reduce((s, r) => s + (r.verified_payments || 0), 0),
    partner_payment: filteredRecords.reduce((s, r) => s + (r.partner_payment || 0), 0),
    credits: filteredRecords.reduce((s, r) => s + (r.credits || 0), 0),
    debits: filteredRecords.reduce((s, r) => s + (r.debits || 0), 0),
  }), [filteredRecords]);

  // ===== Raw Funds tab rows: union of financial records + raw funds + new clients =====
  const rawRows = useMemo(() => {
    const info = rawData.client_info || {};
    const typeMatch = (t) =>
      clientType === "all" ||
      (clientType === "partner" && t === "Partner") ||
      (clientType === "dp_client" && t === "DP Client") ||
      (clientType === "agent" && t === "Agent");
    const map = new Map();
    const ensure = (cc, m) => {
      const key = `${cc}|${m}`;
      if (!map.has(key)) {
        const ci = info[cc] || {};
        map.set(key, {
          client_code: cc, client_type: ci.type || "Partner", client_name: ci.name || cc,
          month: m, month_name: MONTH_LABELS[m] || m,
          parcels: 0, costing: 0, sales: 0, raw_funds: 0, crdr: 0, is_new: false,
        });
      }
      return map.get(key);
    };
    records.forEach((r) => {
      const row = ensure(r.client_code, r.month);
      row.parcels = r.parcels || 0;
      row.costing = r.costing || 0;
      row.sales = r.sales || 0;
      row.client_type = r.client_type;
    });
    (rawData.raw_funds || []).forEach((rf) => {
      if (month && rf.month !== Number(month)) return;
      ensure(rf.client_code, rf.month).raw_funds = rf.raw_funds || 0;
    });
    // Manual Cr/Dr entries net (credit +, debit −) — additive to raw funds
    (rawData.crdr_net || []).forEach((cd) => {
      if (month && cd.month !== Number(month)) return;
      ensure(cd.client_code, cd.month).crdr = cd.crdr || 0;
    });
    // Auto rows: newly added partners/clients appear in their creation month
    (rawData.new_clients || []).forEach((nc) => {
      if (month && nc.month !== Number(month)) return;
      ensure(nc.client_code, nc.month).is_new = true;
    });
    // Guarantee: every client with ORDERS that month (from shipment costing) gets a row
    (rawData.order_clients || []).forEach((oc) => {
      if (month && oc.month !== Number(month)) return;
      ensure(oc.client_code, oc.month);
    });
    let rows = [...map.values()].filter((r) => typeMatch(r.client_type));
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      rows = rows.filter((r) => r.client_code.toLowerCase().includes(q) || (r.client_name || "").toLowerCase().includes(q));
    }
    rows.forEach((r) => {
      const effectiveRaw = (r.raw_funds || 0) + (r.crdr || 0);
      r.sale_minus_raw = Math.round((r.sales - effectiveRaw) * 100) / 100;
      r.cost_minus_raw = Math.round((r.costing - effectiveRaw) * 100) / 100;
      // BALANCE = (GP × self share %) + costing − raw funds received
      const ratio1 = (info[r.client_code] || {}).ratio1 || 0;
      const gpSelf = (r.sales - r.costing) * ratio1 / 100;
      r.balance = Math.round((gpSelf + r.costing - effectiveRaw) * 100) / 100;
      r.narration = (rawData.narrations || {})[`${r.client_code}|${r.month}`] || "";
    });
    rows.sort((a, b) => {
      const { key, direction } = rawSortConfig;
      const aVal = a[key] ?? "";
      const bVal = b[key] ?? "";
      let cmp;
      if (typeof aVal === "number" && typeof bVal === "number") cmp = aVal - bVal;
      else cmp = String(aVal).toLowerCase().localeCompare(String(bVal).toLowerCase());
      if (cmp === 0) cmp = a.client_code.localeCompare(b.client_code) || a.month - b.month;
      return direction === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [records, rawData, month, clientType, searchTerm, rawSortConfig]);

  const rawTotals = useMemo(() => ({
    parcels: rawRows.reduce((s, r) => s + (r.parcels || 0), 0),
    costing: rawRows.reduce((s, r) => s + (r.costing || 0), 0),
    sales: rawRows.reduce((s, r) => s + (r.sales || 0), 0),
    raw_funds: rawRows.reduce((s, r) => s + (r.raw_funds || 0), 0),
    crdr: rawRows.reduce((s, r) => s + (r.crdr || 0), 0),
    sale_minus_raw: rawRows.reduce((s, r) => s + (r.sale_minus_raw || 0), 0),
    cost_minus_raw: rawRows.reduce((s, r) => s + (r.cost_minus_raw || 0), 0),
    balance: rawRows.reduce((s, r) => s + (r.balance || 0), 0),
  }), [rawRows]);

  const saveRawNarration = async () => {
    if (!rawNarrEdit) return;
    const { client_code, month: m, value } = rawNarrEdit;
    try {
      const res = await fetch(`${BACKEND_URL}/api/financial-overview/raw-funds-narration`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ client_code, year, month: m, narration: value }),
      });
      if (!res.ok) { toast.error("Failed to save narration"); return; }
      setRawData((prev) => ({ ...prev, narrations: { ...(prev.narrations || {}), [`${client_code}|${m}`]: value } }));
    } catch {
      toast.error("Network error while saving narration");
    } finally {
      setRawNarrEdit(null);
    }
  };

  const handleRawSort = (key) => {
    setRawSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const RawSortIcon = ({ columnKey }) => {
    if (rawSortConfig.key !== columnKey) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return rawSortConfig.direction === "asc"
      ? <ArrowUp className="w-3 h-3 ml-1 text-emerald-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-emerald-600" />;
  };

  const fmt = (v) => {
    if (!v && v !== 0) return "-";
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v);
  };

  const fmtShort = (v) => {
    if (!v && v !== 0) return "-";
    const abs = Math.abs(v);
    if (abs >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
    if (abs >= 100000) return `${(v / 100000).toFixed(1)}L`;
    if (abs >= 1000) return `${(v / 1000).toFixed(1)}K`;
    return Math.round(v).toLocaleString("en-IN");
  };

  const activeFilterCount = [clientType !== "all", month !== "", searchTerm !== "", settlementStatus !== "all"].filter(Boolean).length;

  const getStatusBadge = (status) => {
    const cfgs = {
      pending: { label: "Pending", variant: "outline", icon: Clock, color: "text-amber-600" },
      partial: { label: "Partial", variant: "secondary", icon: AlertTriangle, color: "text-blue-600" },
      complete: { label: "Complete", variant: "default", icon: CheckCircle, color: "text-green-600" },
      disputed: { label: "Disputed", variant: "destructive", icon: XCircle, color: "text-red-600" },
    };
    const c = cfgs[status] || cfgs.pending;
    const Icon = c.icon;
    return <Badge variant={c.variant} className={`${c.color} gap-1`}><Icon className="w-3 h-3" />{c.label}</Badge>;
  };

  const getRowBg = (status) => {
    switch (status) {
      case "complete": return "bg-green-50/50";
      case "partial": return "bg-blue-50/50";
      case "disputed": return "bg-red-50/50";
      default: return "";
    }
  };

  // Determine dynamic GP columns from filtered records
  const gpColumns = useMemo(() => {
    // Collect all unique GP distribution entries
    const columnMap = new Map(); // index -> {name, percentage, is_self}
    for (const r of filteredRecords) {
      const dist = r.gp_distribution || [];
      dist.forEach((d, i) => {
        const existing = columnMap.get(i);
        if (!existing || d.name !== "Self") {
          columnMap.set(i, { name: d.name, percentage: d.percentage, is_self: d.is_self });
        }
      });
    }
    return Array.from(columnMap.entries()).sort((a, b) => a[0] - b[0]).map(([, v]) => v);
  }, [filteredRecords]);

  // Export CSV with new columns
  const exportToCSV = () => {
    if (activeTab === "raw-funds") {
      const headers = ["Client", "Type", "Month", "Parcels", "Costing", "Sales", "Raw Funds", "Cr/Dr", "Sale - Raw Funds", "Cost - Raw Funds", "Balance", "Narration"];
      const rows = rawRows.map((r) => [r.client_code, r.client_type, r.month_name, r.parcels, r.costing, r.sales, r.raw_funds, r.crdr, r.sale_minus_raw, r.cost_minus_raw, r.balance, `"${(r.narration || "").replace(/"/g, '""')}"`]);
      const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `raw_funds_overview_${year}${month ? `_${month}` : ""}.csv`;
      a.click();
      return;
    }
    const gpHeaders = gpColumns.map((g) => `GP ${g.name} (${g.percentage}%)`);
    const netHeaders = gpColumns.filter((g) => !g.is_self).map((g) => `Net ${g.name}`);
    const headers = ["Client", "Type", "Month", "Parcels", "Costing", "Sales", "Total GP", ...gpHeaders, "Verified Payments", "Credits", "Debits", "Partner Payment", ...netHeaders, "Status", "Narration"];
    const rows = filteredRecords.map((r) => {
      const dist = r.gp_distribution || [];
      const gpVals = gpColumns.map((_, i) => dist[i]?.amount || 0);
      const netVals = gpColumns.filter((g) => !g.is_self).map((_, i) => {
        const pIdx = gpColumns.findIndex((g, gi) => !g.is_self && gpColumns.slice(0, gi).filter((x) => !x.is_self).length === i);
        return dist[pIdx]?.net_position || 0;
      });
      return [r.client_code, r.client_type, r.month_name, r.parcels, r.costing, r.sales, r.total_gp, ...gpVals, r.verified_payments, r.credits, r.debits, r.partner_payment, ...netVals, r.settlement_status, r.narration];
    });
    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `financial_overview_${year}${month ? `_${month}` : ""}.csv`;
    a.click();
  };

  // Get unique partners from filtered records (active tab aware)
  const uniquePartners = useMemo(() => {
    const src = activeTab === "raw-funds" ? rawRows : filteredRecords;
    return [...new Set(src.map((r) => r.client_code))].sort();
  }, [filteredRecords, rawRows, activeTab]);

  // Toggle partner in print selection
  const togglePrintPartner = (code) => {
    setPrintPartners((prev) => {
      if (code === "all") return ["all"];
      const next = prev.filter((p) => p !== "all");
      if (next.includes(code)) {
        const result = next.filter((p) => p !== code);
        return result.length === 0 ? ["all"] : result;
      }
      const result = [...next, code];
      return result.length === uniquePartners.length ? ["all"] : result;
    });
  };

  // Open print view in new tab
  const openPrintView = () => {
    const monthLabel = month ? ` / ${MONTH_LABELS[Number(month)]}` : "";
    if (activeTab === "raw-funds") {
      const recs = printPartners.includes("all")
        ? rawRows
        : rawRows.filter((r) => printPartners.includes(r.client_code));
      if (recs.length === 0) {
        toast.error("No records to print");
        return;
      }
      const printData = {
        mode: "raw_funds",
        records: recs,
        columns: printRawColumns,
        year,
        month,
        title: `Raw Funds Overview - ${year}${monthLabel}`,
        partners: printPartners.includes("all") ? "All Partners" : printPartners.join(", "),
      };
      localStorage.setItem("fo_print_data", JSON.stringify(printData));
      window.open(`${window.location.origin}/financial-overview/print`, "_blank");
      setPrintDialog(false);
      return;
    }
    const printRecords = printPartners.includes("all")
      ? filteredRecords
      : filteredRecords.filter((r) => printPartners.includes(r.client_code));

    if (printRecords.length === 0) {
      toast.error("No records to print");
      return;
    }

    // Build print data
    const printData = {
      records: printRecords,
      columns: printColumns,
      gpColumns,
      year,
      month,
      title: `Financial Overview - ${year}${month ? ` / ${MONTH_LABELS[Number(month)]}` : ""}`,
      partners: printPartners.includes("all") ? "All Partners" : printPartners.join(", "),
      fmt: "INR",
    };

    localStorage.setItem("fo_print_data", JSON.stringify(printData));
    window.open(`${window.location.origin}/financial-overview/print`, "_blank");
    setPrintDialog(false);
  };

  if (loading && records.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6 p-3 lg:p-6 max-w-full overflow-x-hidden" data-testid="financial-overview-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-slate-800 flex items-center gap-2">
            <DollarSign className="w-6 h-6 lg:w-7 lg:h-7 text-emerald-600" />
            Financial Overview
          </h1>
          <p className="text-slate-500 text-xs lg:text-sm mt-1">GP Distribution, Payments & Net Position</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="h-8 text-xs lg:text-sm" data-testid="refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button variant="outline" size="sm" onClick={exportToCSV} className="h-8 text-xs lg:text-sm" data-testid="export-btn">
            <Download className="w-3.5 h-3.5 mr-1" />
            <span className="hidden sm:inline">Export</span>
          </Button>
          <Button variant="default" size="sm" onClick={() => setPrintDialog(true)} className="h-8 text-xs lg:text-sm bg-emerald-600 hover:bg-emerald-700" data-testid="print-btn">
            <Printer className="w-3.5 h-3.5 mr-1" />
            <span className="hidden sm:inline">Print</span>
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="lg:overflow-visible overflow-hidden">
        <div className="overflow-x-auto pb-2 -mx-3 px-3 lg:mx-0 lg:px-0">
          <div className="inline-flex lg:grid lg:grid-cols-8 gap-2 lg:gap-3">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 min-w-[100px] lg:min-w-0">
              <CardContent className="p-2 lg:p-3">
                <p className="text-[10px] lg:text-xs text-blue-600 font-medium">Parcels</p>
                <p className="text-sm lg:text-lg font-bold text-blue-800">{totals.parcels?.toLocaleString() || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200 min-w-[100px] lg:min-w-0">
              <CardContent className="p-2 lg:p-3">
                <p className="text-[10px] lg:text-xs text-amber-600 font-medium">Costing</p>
                <p className="text-sm lg:text-lg font-bold text-amber-800">{fmtShort(totals.costing)}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200 min-w-[100px] lg:min-w-0">
              <CardContent className="p-2 lg:p-3">
                <p className="text-[10px] lg:text-xs text-emerald-600 font-medium">Sales</p>
                <p className="text-sm lg:text-lg font-bold text-emerald-800">{fmtShort(totals.sales)}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 min-w-[100px] lg:min-w-0">
              <CardContent className="p-2 lg:p-3">
                <p className="text-[10px] lg:text-xs text-green-600 font-medium">Total GP</p>
                <p className="text-sm lg:text-lg font-bold text-green-800">{fmtShort(totals.total_gp)}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 min-w-[100px] lg:min-w-0">
              <CardContent className="p-2 lg:p-3">
                <p className="text-[10px] lg:text-xs text-purple-600 font-medium">Verified Payments</p>
                <p className="text-sm lg:text-lg font-bold text-purple-800">{fmtShort(totals.verified_payments)}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200 min-w-[100px] lg:min-w-0">
              <CardContent className="p-2 lg:p-3">
                <p className="text-[10px] lg:text-xs text-teal-600 font-medium">Credits</p>
                <p className="text-sm lg:text-lg font-bold text-teal-800">{fmtShort(totals.credits)}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-rose-50 to-rose-100 border-rose-200 min-w-[100px] lg:min-w-0">
              <CardContent className="p-2 lg:p-3">
                <p className="text-[10px] lg:text-xs text-rose-600 font-medium">Debits</p>
                <p className="text-sm lg:text-lg font-bold text-rose-800">{fmtShort(totals.debits)}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 min-w-[100px] lg:min-w-0">
              <CardContent className="p-2 lg:p-3">
                <p className="text-[10px] lg:text-xs text-orange-600 font-medium">Partner Payment</p>
                <p className="text-sm lg:text-lg font-bold text-orange-800">{fmtShort(totals.partner_payment)}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 lg:p-4">
          <div className="lg:hidden">
            <button onClick={() => setFiltersExpanded(!filtersExpanded)} className="w-full flex items-center justify-between p-2 -m-2 rounded-lg hover:bg-slate-50" data-testid="filter-toggle-btn">
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4 text-slate-500" />
                <span className="text-xs font-medium text-slate-700">Filters</span>
                {activeFilterCount > 0 && <span className="bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{activeFilterCount}</span>}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">{filteredRecords.length} records</span>
                {filtersExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </div>
            </button>
            <div className={`overflow-hidden transition-all duration-200 ${filtersExpanded ? "max-h-96 opacity-100 mt-3" : "max-h-0 opacity-0"}`}>
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                    <SelectTrigger className="h-9 text-xs"><Calendar className="w-3 h-3 mr-1" /><SelectValue /></SelectTrigger>
                    <SelectContent>{[2024, 2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={month || "all"} onValueChange={(v) => setMonth(v === "all" ? "" : v)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="All Months" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Months</SelectItem>
                      {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Select value={clientType} onValueChange={setClientType}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="partner">Partners</SelectItem>
                    <SelectItem value="dp_client">DP Clients</SelectItem>
                    <SelectItem value="agent">Agents</SelectItem>
                  </SelectContent>
                </Select>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input placeholder="Search client..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 h-9 text-xs" data-testid="search-input" />
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Filters */}
          <div className="hidden lg:flex lg:items-center lg:gap-3">
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-[100px] h-9"><Calendar className="w-3.5 h-3.5 mr-1" /><SelectValue /></SelectTrigger>
              <SelectContent>{[2024, 2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={month || "all"} onValueChange={(v) => setMonth(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[120px] h-9"><SelectValue placeholder="All Months" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Months</SelectItem>
                {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={clientType} onValueChange={setClientType}>
              <SelectTrigger className="w-[130px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="partner">Partners</SelectItem>
                <SelectItem value="dp_client">DP Clients</SelectItem>
                <SelectItem value="agent">Agents</SelectItem>
              </SelectContent>
            </Select>
            <Select value={settlementStatus} onValueChange={setSettlementStatus}>
              <SelectTrigger className="w-[130px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="complete">Complete</SelectItem>
                <SelectItem value="disputed">Disputed</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative flex-1 min-w-[150px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search client..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 h-9" data-testid="search-input-desktop" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table with subpanels */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview" className="text-xs lg:text-sm" data-testid="tab-overview">
            <DollarSign className="w-3.5 h-3.5 mr-1" />
            Financial Records
          </TabsTrigger>
          <TabsTrigger value="raw-funds" className="text-xs lg:text-sm" data-testid="tab-raw-funds">
            <Banknote className="w-3.5 h-3.5 mr-1" />
            Raw Funds
          </TabsTrigger>
        </TabsList>

        <TabsContent value="raw-funds" className="mt-3">
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-base flex items-center justify-between">
                <span data-testid="raw-records-count">Raw Funds Records ({rawRows.length})</span>
                {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {rawRows.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Banknote className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No raw funds records found</p>
                  <p className="text-sm">Adjust filters or upload raw funds data in Payment Verification</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="raw-funds-table">
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="cursor-pointer hover:bg-slate-100 sticky left-0 bg-slate-50 z-10 min-w-[110px]" onClick={() => handleRawSort("client_code")}>
                          <div className="flex items-center text-xs">Client<RawSortIcon columnKey="client_code" /></div>
                        </TableHead>
                        <TableHead className="cursor-pointer hover:bg-slate-100 min-w-[50px]" onClick={() => handleRawSort("month")}>
                          <div className="flex items-center text-xs">Month<RawSortIcon columnKey="month" /></div>
                        </TableHead>
                        <TableHead className="text-right cursor-pointer hover:bg-slate-100 min-w-[60px]" onClick={() => handleRawSort("parcels")}>
                          <div className="flex items-center justify-end text-xs">Parcels<RawSortIcon columnKey="parcels" /></div>
                        </TableHead>
                        <TableHead className="text-right cursor-pointer hover:bg-slate-100 min-w-[90px]" onClick={() => handleRawSort("costing")}>
                          <div className="flex items-center justify-end text-xs">Costing<RawSortIcon columnKey="costing" /></div>
                        </TableHead>
                        <TableHead className="text-right cursor-pointer hover:bg-slate-100 min-w-[90px]" onClick={() => handleRawSort("sales")}>
                          <div className="flex items-center justify-end text-xs">Sales<RawSortIcon columnKey="sales" /></div>
                        </TableHead>
                        <TableHead className="text-right cursor-pointer hover:bg-slate-100 min-w-[100px] bg-cyan-50" onClick={() => handleRawSort("raw_funds")}>
                          <div className="flex items-center justify-end text-xs font-semibold text-cyan-700">Raw Funds<RawSortIcon columnKey="raw_funds" /></div>
                        </TableHead>
                        <TableHead className="text-right cursor-pointer hover:bg-slate-100 min-w-[95px] bg-violet-50" onClick={() => handleRawSort("crdr")}>
                          <div className="flex items-center justify-end text-xs font-semibold text-violet-700">Cr/Dr<RawSortIcon columnKey="crdr" /></div>
                        </TableHead>
                        <TableHead className="text-right cursor-pointer hover:bg-slate-100 min-w-[110px] bg-emerald-50" onClick={() => handleRawSort("sale_minus_raw")}>
                          <div className="flex items-center justify-end text-xs font-semibold text-emerald-700">Sale − Raw Funds<RawSortIcon columnKey="sale_minus_raw" /></div>
                        </TableHead>
                        <TableHead className="text-right cursor-pointer hover:bg-slate-100 min-w-[110px] bg-amber-50" onClick={() => handleRawSort("cost_minus_raw")}>
                          <div className="flex items-center justify-end text-xs font-semibold text-amber-700">Cost − Raw Funds<RawSortIcon columnKey="cost_minus_raw" /></div>
                        </TableHead>
                        <TableHead
                          className="text-right cursor-pointer hover:bg-slate-100 min-w-[105px] bg-indigo-50"
                          onClick={() => handleRawSort("balance")}
                          title="Balance = (Sales − Cost) × Self share % + Cost − Raw Funds received"
                        >
                          <div className="flex items-center justify-end text-xs font-semibold text-indigo-700">Balance<RawSortIcon columnKey="balance" /></div>
                        </TableHead>
                        <TableHead className="min-w-[150px]">
                          <span className="text-xs font-semibold text-slate-600">Narration</span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rawRows.map((r, idx) => (
                        <TableRow key={`${r.client_code}-${r.month}-${idx}`} className={r.is_new ? "bg-blue-50/50" : ""} data-testid={`raw-row-${r.client_code}-${r.month}`}>
                          <TableCell className="sticky left-0 bg-white z-10 font-medium">
                            <span className="font-semibold text-sm">{r.client_code}</span>
                            <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0">{r.client_type}</Badge>
                            {r.is_new && <Badge className="ml-1 text-[9px] px-1 py-0 bg-blue-500">NEW</Badge>}
                          </TableCell>
                          <TableCell className="text-xs text-slate-600">{r.month_name}</TableCell>
                          <TableCell className="text-right text-sm">{r.parcels || "-"}</TableCell>
                          <TableCell className="text-right text-sm text-amber-700">{r.costing ? fmt(r.costing) : "-"}</TableCell>
                          <TableCell className="text-right text-sm text-emerald-700 font-medium">{r.sales ? fmt(r.sales) : "-"}</TableCell>
                          <TableCell className="text-right text-sm font-bold text-cyan-700 bg-cyan-50/30">{r.raw_funds ? fmt(r.raw_funds) : "-"}</TableCell>
                          <TableCell className={`text-right text-sm font-bold bg-violet-50/30 ${r.crdr > 0 ? "text-emerald-700" : r.crdr < 0 ? "text-red-600" : "text-slate-400"}`}>
                            {r.crdr ? fmt(r.crdr) : "-"}
                          </TableCell>
                          <TableCell className={`text-right text-sm font-medium bg-emerald-50/30 ${r.sale_minus_raw >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                            {r.sales || r.raw_funds || r.crdr ? fmt(r.sale_minus_raw) : "-"}
                          </TableCell>
                          <TableCell className={`text-right text-sm font-medium bg-amber-50/30 ${r.cost_minus_raw >= 0 ? "text-amber-700" : "text-red-600"}`}>
                            {r.costing || r.raw_funds || r.crdr ? fmt(r.cost_minus_raw) : "-"}
                          </TableCell>
                          <TableCell className={`text-right text-sm font-bold bg-indigo-50/30 ${r.balance > 0 ? "text-red-600" : r.balance < 0 ? "text-emerald-700" : "text-slate-500"}`}>
                            {r.sales || r.costing || r.raw_funds || r.crdr ? fmt(r.balance) : "-"}
                          </TableCell>
                          <TableCell
                            className="text-xs text-slate-600 cursor-pointer hover:bg-teal-50/50 max-w-[220px]"
                            onDoubleClick={() => setRawNarrEdit({ key: `${r.client_code}|${r.month}`, client_code: r.client_code, month: r.month, value: r.narration || "" })}
                            title="Double-click to edit narration"
                            data-testid={`raw-narration-${r.client_code}-${r.month}`}
                          >
                            {rawNarrEdit?.key === `${r.client_code}|${r.month}` ? (
                              <input
                                autoFocus
                                value={rawNarrEdit.value}
                                onChange={(e) => setRawNarrEdit((p) => ({ ...p, value: e.target.value }))}
                                onBlur={saveRawNarration}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") saveRawNarration();
                                  if (e.key === "Escape") setRawNarrEdit(null);
                                }}
                                className="w-full border border-teal-400 rounded px-1.5 py-0.5 text-xs focus:outline-none"
                                data-testid="raw-narration-input"
                              />
                            ) : (
                              <span className={r.narration ? "" : "text-slate-300"}>{r.narration || "—"}</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Totals Row */}
                      <TableRow className="bg-slate-100 border-t-2 border-slate-300 font-bold" data-testid="raw-funds-totals-row">
                        <TableCell className="sticky left-0 bg-slate-100 z-10 font-bold text-sm">TOTAL</TableCell>
                        <TableCell></TableCell>
                        <TableCell className="text-right text-sm">{rawTotals.parcels.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-sm text-amber-800">{fmt(rawTotals.costing)}</TableCell>
                        <TableCell className="text-right text-sm text-emerald-800">{fmt(rawTotals.sales)}</TableCell>
                        <TableCell className="text-right text-sm text-cyan-800 bg-cyan-100/50">{fmt(rawTotals.raw_funds)}</TableCell>
                        <TableCell className={`text-right text-sm bg-violet-100/50 ${rawTotals.crdr >= 0 ? "text-emerald-800" : "text-red-600"}`}>{fmt(rawTotals.crdr)}</TableCell>
                        <TableCell className={`text-right text-sm bg-emerald-100/50 ${rawTotals.sale_minus_raw >= 0 ? "text-emerald-800" : "text-red-600"}`}>{fmt(rawTotals.sale_minus_raw)}</TableCell>
                        <TableCell className={`text-right text-sm bg-amber-100/50 ${rawTotals.cost_minus_raw >= 0 ? "text-amber-800" : "text-red-600"}`}>{fmt(rawTotals.cost_minus_raw)}</TableCell>
                        <TableCell className={`text-right text-sm bg-indigo-100/50 font-bold ${rawTotals.balance > 0 ? "text-red-600" : "text-emerald-800"}`}>{fmt(rawTotals.balance)}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="overview" className="mt-3">
      <Card>
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-base flex items-center justify-between">
            <span data-testid="records-count">Financial Records ({filteredRecords.length})</span>
            {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredRecords.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <DollarSign className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No financial records found</p>
              <p className="text-sm">Adjust filters or select a different year</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="financial-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="cursor-pointer hover:bg-slate-100 sticky left-0 bg-slate-50 z-10 min-w-[100px]" onClick={() => handleSort("client_code")}>
                      <div className="flex items-center text-xs">Client<SortIcon columnKey="client_code" /></div>
                    </TableHead>
                    <TableHead className="cursor-pointer hover:bg-slate-100 min-w-[50px]" onClick={() => handleSort("month")}>
                      <div className="flex items-center text-xs">Month<SortIcon columnKey="month" /></div>
                    </TableHead>
                    <TableHead className="text-right cursor-pointer hover:bg-slate-100 min-w-[60px]" onClick={() => handleSort("parcels")}>
                      <div className="flex items-center justify-end text-xs">Parcels<SortIcon columnKey="parcels" /></div>
                    </TableHead>
                    <TableHead className="text-right cursor-pointer hover:bg-slate-100 min-w-[80px]" onClick={() => handleSort("costing")}>
                      <div className="flex items-center justify-end text-xs">Costing<SortIcon columnKey="costing" /></div>
                    </TableHead>
                    <TableHead className="text-right cursor-pointer hover:bg-slate-100 min-w-[80px]" onClick={() => handleSort("sales")}>
                      <div className="flex items-center justify-end text-xs">Sales<SortIcon columnKey="sales" /></div>
                    </TableHead>
                    <TableHead className="text-right cursor-pointer hover:bg-slate-100 min-w-[80px] bg-green-50" onClick={() => handleSort("total_gp")}>
                      <div className="flex items-center justify-end text-xs font-semibold text-green-700">Total GP<SortIcon columnKey="total_gp" /></div>
                    </TableHead>
                    {/* Dynamic GP columns */}
                    {gpColumns.map((col, i) => (
                      <TableHead key={`gp-${i}`} className={`text-right min-w-[90px] ${col.is_self ? "bg-emerald-50" : "bg-violet-50"}`}>
                        <div className="text-xs">
                          <span className={`font-semibold ${col.is_self ? "text-emerald-700" : "text-violet-700"}`}>
                            GP {col.name}
                          </span>
                          <span className="block text-[9px] opacity-70">({col.percentage}%)</span>
                        </div>
                      </TableHead>
                    ))}
                    <TableHead className="text-right cursor-pointer hover:bg-slate-100 min-w-[90px] bg-purple-50" onClick={() => handleSort("verified_payments")}>
                      <div className="flex items-center justify-end text-xs text-purple-700 font-semibold">Payments<SortIcon columnKey="verified_payments" /></div>
                    </TableHead>
                    <TableHead className="text-right cursor-pointer hover:bg-slate-100 min-w-[70px] bg-teal-50" onClick={() => handleSort("credits")}>
                      <div className="flex items-center justify-end text-xs text-teal-700">Credit<SortIcon columnKey="credits" /></div>
                    </TableHead>
                    <TableHead className="text-right cursor-pointer hover:bg-slate-100 min-w-[70px] bg-rose-50" onClick={() => handleSort("debits")}>
                      <div className="flex items-center justify-end text-xs text-rose-700">Debit<SortIcon columnKey="debits" /></div>
                    </TableHead>
                    <TableHead className="text-right cursor-pointer hover:bg-slate-100 min-w-[90px] bg-orange-50" onClick={() => handleSort("partner_payment")}>
                      <div className="flex items-center justify-end text-xs text-orange-700">Partner Pmt<SortIcon columnKey="partner_payment" /></div>
                    </TableHead>
                    {/* Net Position columns for each non-self partner */}
                    {gpColumns.filter((c) => !c.is_self).map((col, i) => (
                      <TableHead key={`net-${i}`} className="text-right min-w-[90px] bg-indigo-50">
                        <div className="text-xs">
                          <span className="font-semibold text-indigo-700">Net {col.name}</span>
                        </div>
                      </TableHead>
                    ))}
                    <TableHead className="text-center min-w-[80px]">Status</TableHead>
                    <TableHead className="text-center min-w-[50px]">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRecords.map((r, idx) => {
                    const dist = r.gp_distribution || [];
                    const gpValid = r.gp_valid;
                    return (
                      <TableRow key={`${r.client_code}-${r.month}-${idx}`} className={getRowBg(r.settlement_status)} data-testid={`row-${r.client_code}-${r.month}`}>
                        <TableCell className="sticky left-0 bg-white z-10 font-medium">
                          <div>
                            <span className="font-semibold text-sm">{r.client_code}</span>
                            <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0">{r.client_type}</Badge>
                          </div>
                          {!gpValid && (
                            <span className="text-[9px] text-red-500 font-medium">% not valid ({r.gp_total_percentage}%)</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-slate-600">{r.month_name}</TableCell>
                        <TableCell className="text-right text-sm">{r.parcels || "-"}</TableCell>
                        <TableCell className="text-right text-sm text-amber-700">{r.costing ? fmt(r.costing) : "-"}</TableCell>
                        <TableCell className="text-right text-sm text-emerald-700 font-medium">{r.sales ? fmt(r.sales) : "-"}</TableCell>
                        <TableCell className={`text-right text-sm font-bold bg-green-50/50 ${r.total_gp >= 0 ? "text-green-700" : "text-red-600"}`}>
                          {r.total_gp ? fmt(r.total_gp) : "-"}
                        </TableCell>
                        {/* Dynamic GP amounts */}
                        {gpColumns.map((col, i) => {
                          const d = dist[i];
                          return (
                            <TableCell key={`gp-${i}`} className={`text-right text-sm ${col.is_self ? "bg-emerald-50/30" : "bg-violet-50/30"}`}>
                              {d ? (
                                <span className={`font-medium ${d.amount >= 0 ? (col.is_self ? "text-emerald-700" : "text-violet-700") : "text-red-600"}`}>
                                  {fmt(d.amount)}
                                </span>
                              ) : "-"}
                            </TableCell>
                          );
                        })}
                        <TableCell className="text-right text-sm font-medium text-purple-700 bg-purple-50/30">
                          {r.verified_payments ? fmt(r.verified_payments) : "-"}
                        </TableCell>
                        <TableCell className="text-right text-sm text-teal-700 bg-teal-50/30">
                          {r.credits ? fmt(r.credits) : "-"}
                        </TableCell>
                        <TableCell className="text-right text-sm text-rose-700 bg-rose-50/30">
                          {r.debits ? fmt(r.debits) : "-"}
                        </TableCell>
                        <TableCell className="text-right text-sm text-orange-700 bg-orange-50/30">
                          {r.partner_payment ? fmt(r.partner_payment) : "-"}
                        </TableCell>
                        {/* Net Position for each non-self partner */}
                        {gpColumns.filter((c) => !c.is_self).map((col, i) => {
                          const pIdx = gpColumns.findIndex((g, gi) => !g.is_self && gpColumns.slice(0, gi).filter((x) => !x.is_self).length === i);
                          const d = dist[pIdx];
                          return (
                            <TableCell key={`net-${i}`} className="text-right text-sm font-bold bg-indigo-50/30">
                              {d ? (
                                <span className={d.net_position >= 0 ? "text-green-700" : "text-red-600"}>
                                  {fmt(d.net_position)}
                                </span>
                              ) : "-"}
                            </TableCell>
                          );
                        })}
                        <TableCell className="text-center">{getStatusBadge(r.settlement_status)}</TableCell>
                        <TableCell className="text-center">
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(r)} data-testid={`edit-${r.client_code}-${r.month}`}>
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Totals Row */}
                  <TableRow className="bg-slate-100 border-t-2 border-slate-300 font-bold">
                    <TableCell className="sticky left-0 bg-slate-100 z-10 font-bold text-sm">TOTAL</TableCell>
                    <TableCell></TableCell>
                    <TableCell className="text-right text-sm">{totals.parcels.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-sm text-amber-800">{fmt(totals.costing)}</TableCell>
                    <TableCell className="text-right text-sm text-emerald-800">{fmt(totals.sales)}</TableCell>
                    <TableCell className="text-right text-sm text-green-800 bg-green-100/50">{fmt(totals.total_gp)}</TableCell>
                    {gpColumns.map((_, i) => <TableCell key={`gp-t-${i}`} className="text-right text-sm">-</TableCell>)}
                    <TableCell className="text-right text-sm text-purple-800">{fmt(totals.verified_payments)}</TableCell>
                    <TableCell className="text-right text-sm text-teal-800">{fmt(totals.credits)}</TableCell>
                    <TableCell className="text-right text-sm text-rose-800">{fmt(totals.debits)}</TableCell>
                    <TableCell className="text-right text-sm text-orange-800">{fmt(totals.partner_payment)}</TableCell>
                    {gpColumns.filter((c) => !c.is_self).map((_, i) => <TableCell key={`net-t-${i}`} className="text-right text-sm">-</TableCell>)}
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>

      {/* Print/Export Dialog */}
      <Dialog open={printDialog} onOpenChange={setPrintDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-emerald-600" />
              Print / Export {activeTab === "raw-funds" ? "Raw Funds" : "Financial Overview"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Partner Selection */}
            <div>
              <Label className="text-sm font-semibold mb-2 block">Select Partners</Label>
              <div className="border rounded-lg p-3 max-h-[160px] overflow-y-auto space-y-1.5">
                <label className="flex items-center gap-2 cursor-pointer py-1 px-1 rounded hover:bg-slate-50">
                  <Checkbox
                    checked={printPartners.includes("all")}
                    onCheckedChange={() => togglePrintPartner("all")}
                    data-testid="print-partner-all"
                  />
                  <span className="text-sm font-medium">All Partners ({uniquePartners.length})</span>
                </label>
                <div className="border-t my-1" />
                {uniquePartners.map((p) => (
                  <label key={p} className="flex items-center gap-2 cursor-pointer py-0.5 px-1 rounded hover:bg-slate-50">
                    <Checkbox
                      checked={printPartners.includes("all") || printPartners.includes(p)}
                      onCheckedChange={() => togglePrintPartner(p)}
                      data-testid={`print-partner-${p}`}
                    />
                    <span className="text-sm">{p}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Column Selection */}
            <div>
              <Label className="text-sm font-semibold mb-2 block">Select Columns</Label>
              <div className="grid grid-cols-2 gap-1.5 border rounded-lg p-3">
                {(activeTab === "raw-funds" ? [
                  { key: "client", label: "Client" },
                  { key: "month", label: "Month" },
                  { key: "parcels", label: "Parcels" },
                  { key: "costing", label: "Costing" },
                  { key: "sales", label: "Sales" },
                  { key: "raw_funds", label: "Raw Funds" },
                  { key: "sale_minus_raw", label: "Sale − Raw Funds" },
                  { key: "cost_minus_raw", label: "Cost − Raw Funds" },
                ] : [
                  { key: "client", label: "Client" },
                  { key: "month", label: "Month" },
                  { key: "parcels", label: "Parcels" },
                  { key: "costing", label: "Costing" },
                  { key: "sales", label: "Sales" },
                  { key: "total_gp", label: "Total GP" },
                  { key: "gp_columns", label: "GP Distribution" },
                  { key: "payments", label: "Payments" },
                  { key: "credits", label: "Credits" },
                  { key: "debits", label: "Debits" },
                  { key: "partner_payment", label: "Partner Payment" },
                  { key: "net_position", label: "Net Position" },
                  { key: "status", label: "Status" },
                  { key: "narration", label: "Narration" },
                ]).map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer py-0.5 px-1 rounded hover:bg-slate-50">
                    <Checkbox
                      checked={activeTab === "raw-funds" ? printRawColumns[key] : printColumns[key]}
                      onCheckedChange={(checked) =>
                        (activeTab === "raw-funds" ? setPrintRawColumns : setPrintColumns)((prev) => ({ ...prev, [key]: !!checked }))
                      }
                      data-testid={`print-col-${key}`}
                    />
                    <span className="text-sm">{label}</span>
                  </label>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => (activeTab === "raw-funds"
                    ? setPrintRawColumns(Object.fromEntries(Object.keys(printRawColumns).map((k) => [k, true])))
                    : setPrintColumns(Object.fromEntries(Object.keys(printColumns).map((k) => [k, true]))))}
                  className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  Select All
                </button>
                <span className="text-slate-300">|</span>
                <button
                  onClick={() => (activeTab === "raw-funds"
                    ? setPrintRawColumns(Object.fromEntries(Object.keys(printRawColumns).map((k) => [k, false])))
                    : setPrintColumns(Object.fromEntries(Object.keys(printColumns).map((k) => [k, false]))))}
                  className="text-xs text-slate-500 hover:text-slate-700 font-medium"
                >
                  Deselect All
                </button>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPrintDialog(false)}>Cancel</Button>
            <Button onClick={openPrintView} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="open-print-view-btn">
              <Printer className="w-4 h-4" />
              Open Print View
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Settlement Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-emerald-600" />
              Settlement - {editingRecord?.client_code} ({editingRecord?.month_name})
            </DialogTitle>
          </DialogHeader>
          {editingRecord && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-2 bg-slate-50 rounded">
                  <p className="text-xs text-slate-500">Costing</p>
                  <p className="font-semibold">{fmt(editingRecord.costing)}</p>
                </div>
                <div className="p-2 bg-slate-50 rounded">
                  <p className="text-xs text-slate-500">Sales</p>
                  <p className="font-semibold">{fmt(editingRecord.sales)}</p>
                </div>
                <div className="p-2 bg-green-50 rounded">
                  <p className="text-xs text-green-600">Total GP</p>
                  <p className="font-semibold text-green-700">{fmt(editingRecord.total_gp)}</p>
                </div>
                <div className="p-2 bg-purple-50 rounded">
                  <p className="text-xs text-purple-600">Verified Payments</p>
                  <p className="font-semibold text-purple-700">{fmt(editingRecord.verified_payments)}</p>
                </div>
              </div>
              {/* GP Distribution */}
              {editingRecord.gp_distribution?.length > 0 && (
                <div className="p-3 bg-violet-50 rounded-lg border border-violet-200">
                  <p className="text-xs font-semibold text-violet-700 mb-2">GP Distribution</p>
                  {editingRecord.gp_distribution.map((d, i) => (
                    <div key={i} className="flex justify-between text-sm py-1">
                      <span className="text-violet-600">{d.name} ({d.percentage}%)</span>
                      <span className="font-semibold">{fmt(d.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <Label className="text-sm">Status</Label>
                <Select value={editStatus} onValueChange={setEditStatus}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="complete">Complete</SelectItem>
                    <SelectItem value="disputed">Disputed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Narration / Notes</Label>
                <Textarea value={editNarration} onChange={(e) => setEditNarration(e.target.value)} placeholder="Add settlement notes..." className="mt-1" rows={3} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveSettlement} className="bg-emerald-600 hover:bg-emerald-700">Save Settlement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinancialOverviewPage;
